import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { ExternalLink } from "@/components/ExternalLink";
import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { useEnableMetaAdsIntegration } from "@/features/meta-ads/useEnableMetaAdsIntegration";
import { UnifyKeyLabel } from "@/features/unify-scope-setting/UnifyKeyLabel";
import { interlude } from "@/interlude";
import { ECOMMERCE_PLATFORMS } from "@/pages/applications/ecommercePlatforms";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px 24px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  flow: {
    display: "grid",
    gridTemplateColumns:
      "minmax(0, 1fr) 24px minmax(0, 0.8fr) 24px minmax(0, 1fr)",
    alignItems: "center",
    gap: "12px",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: "40px",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: theme.colors.neutral010,
  },
  rowIcon: {
    display: "inline-flex",
    "& svg": {
      fontSize: "1.125rem",
      color: theme.colors.staticFgNote,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  missingRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px dashed ${theme.colors.neutral050}`,
    backgroundColor: theme.colors.white,
  },
  missingIcon: {
    display: "inline-flex",
    paddingTop: "2px",
    opacity: 0.5,
    "& svg": {
      fontSize: "1.125rem",
      color: theme.colors.staticFgNote,
    },
  },
  missingText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  missingTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 8px",
  },
  moreRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors.staticFgLine}`,
  },
  arrow: {
    display: "flex",
    justifyContent: "center",
    color: theme.colors.neutral060,
  },
  hub: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "20px 16px",
    borderRadius: "12px",
    border: `1px solid ${theme.colors.blue020}`,
    backgroundColor: theme.colors.staticBgPrimary,
    textAlign: "center",
  },
  hubIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.blue060,
    color: theme.colors.white,
  },
  keys: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const Row: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}> = function Row({ icon, children, trailing }) {
  return (
    <Box sx={styles.row}>
      <Box component="span" sx={styles.rowIcon}>
        {icon}
      </Box>
      <Box sx={styles.rowText}>
        <Typography variant="body">{children}</Typography>
      </Box>
      {trailing}
    </Box>
  );
};

/**
 * A source the org has not connected yet: dashed, with what connecting it
 * brings and a link to do it. Labelled "Not connected" so it never reads as
 * data the org has.
 */
const MissingSourceRow: React.FC<{
  icon: React.ReactNode;
  name: string;
  benefit: string;
  to: "/channels" | "/applications";
}> = function MissingSourceRow({ icon, name, benefit, to }) {
  const { t } = useTranslation();
  return (
    <Box sx={styles.missingRow}>
      <Box component="span" sx={styles.missingIcon}>
        {icon}
      </Box>
      <Box sx={styles.missingText}>
        <Box sx={styles.missingTitle}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {name}
          </Typography>
          <Badge variant="plain">
            {t("dataHub.flow.missing.label", "Not connected")}
          </Badge>
        </Box>
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {benefit}
        </Typography>
        <RouterLink to={to} variant="body2">
          {t("dataHub.flow.missing.connect", "Connect")}
        </RouterLink>
      </Box>
    </Box>
  );
};

/** Channel types in the order the Channels page groups them, with a label. */
const useChannelGroups = () => {
  const orgChannels = useOrgChannels();
  return useMemo(() => {
    if (!orgChannels.isSuccess) return null;
    const counts = new Map<
      (typeof orgChannels.channels)[number]["type"],
      number
    >();
    for (const channel of orgChannels.channels) {
      counts.set(channel.type, (counts.get(channel.type) ?? 0) + 1);
    }
    return [...counts.entries()].map(([type, count]) => ({ type, count }));
  }, [orgChannels]);
};

/** The sources most brands connect, suggested when missing. */
const SUGGESTED_CHANNELS = ["line", "fb", "ig", "wccs"] as const;
type SuggestedChannel = (typeof SUGGESTED_CHANNELS)[number];

/** Readable channel type names; brand names stay untranslated. */
const channelTypeNames: Record<string, string> = {
  line: "LINE",
  fb: "Facebook",
  ig: "Instagram",
  whatsapp: "WhatsApp",
  wccs: "Web",
  email: "Email",
  sms: "SMS",
};

/**
 * Where the org's customer data comes from and where unified profiles are
 * used, drawn from what is connected today. A destination is listed only when
 * it is in use; nothing is shown as connected that is not.
 */
export const DataFlow: React.FC = function DataFlow() {
  const { t } = useTranslation();
  const channelBenefits: Record<SuggestedChannel, string> = {
    line: t(
      "dataHub.flow.missing.line",
      "Every LINE friend becomes a customer profile",
    ),
    fb: t(
      "dataHub.flow.missing.fb",
      "People who comment or message on Facebook join the same profile",
    ),
    ig: t(
      "dataHub.flow.missing.ig",
      "People who comment or message on Instagram join the same profile",
    ),
    wccs: t(
      "dataHub.flow.missing.web",
      "See which products customers browse on your website",
    ),
  };
  const channelGroups = useChannelGroups();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();
  const googleAdsQuery = interlude.googleAds.useGetManagerAccount({
    queries: { checkAccess: false },
  });
  const enableMetaAds = useEnableMetaAdsIntegration();
  const metaAdsStatusQuery = interlude.metaAds.useGetStatus(undefined, {
    enabled: enableMetaAds,
  });
  const lineAdsGroupQuery = interlude.lineAds.useGetGroup({
    queries: { checkAccess: false },
  });

  if (!unifyScopeSettingQuery.isSuccess) return null;

  const scope = unifyScopeSettingQuery.data;
  const hasMaac = scope.orgs.some((org) => org.source === "maac");
  const hasCaac = scope.orgs.some((org) => org.source === "caac");
  const ecPlatforms = (hasEcModuleQuery.data?.ecPlatforms ?? []).map(
    (type) =>
      ECOMMERCE_PLATFORMS.find((platform) => platform.type === type)?.name ??
      type,
  );
  const adPlatforms = [
    googleAdsQuery.isSuccess ? "Google Ads" : null,
    metaAdsStatusQuery.data?.tokenStatus === "active" ? "Meta Ads" : null,
    lineAdsGroupQuery.isSuccess ? "LINE Ads" : null,
  ].filter((name): name is string => name !== null);
  const apiAccess = [
    organizationSettingQuery.data?.openApiV2Enabled ? "OpenAPI" : null,
    organizationSettingQuery.data?.mcpEnabled ? "MCP" : null,
  ].filter((name): name is string => name !== null);

  // These arrive through the API, which the platform cannot see, so they are
  // offered as options rather than marked connected or missing.
  const moreSources = [
    {
      key: "pos",
      icon: <StorefrontOutlined />,
      name: t("dataHub.flow.more.pos", "POS and in-store purchases"),
      benefit: t(
        "dataHub.flow.more.posBenefit",
        "Add offline orders to the same customer through the Commerce Data API",
      ),
    },
    {
      key: "crm",
      icon: <BadgeOutlined />,
      name: t("dataHub.flow.more.crm", "CRM and membership system"),
      benefit: t(
        "dataHub.flow.more.crmBenefit",
        "Match members by Customer ID across every channel",
      ),
    },
  ];
  const connectedTypes = new Set(channelGroups?.map((group) => group.type));
  // Suggested only once the channel list has loaded, so a slow request never
  // shows a connected channel as missing.
  const missingChannels = channelGroups
    ? SUGGESTED_CHANNELS.filter((type) => !connectedTypes.has(type)).map(
        (type) => ({ type, benefit: channelBenefits[type] }),
      )
    : [];

  return (
    <Box component="section" sx={styles.root}>
      <Typography variant="h3" fontWeight={700}>
        {t("dataHub.flow.title", "How your customer data flows")}
      </Typography>
      <Box sx={styles.flow}>
        <Box sx={styles.column}>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.sources", "Comes from")}
          </Typography>
          {channelGroups?.map((group) => (
            <Row
              key={group.type}
              icon={<ChannelIcon type={group.type} />}
              trailing={<Badge variant="plain">{group.count}</Badge>}
            >
              {channelTypeNames[group.type] ?? group.type}
            </Row>
          ))}
          {ecPlatforms.length > 0 ? (
            <Row icon={<ShoppingBagOutlined />}>{ecPlatforms.join(", ")}</Row>
          ) : null}
          {missingChannels.map((missing) => (
            <MissingSourceRow
              key={missing.type}
              icon={<ChannelIcon type={missing.type} />}
              name={channelTypeNames[missing.type] ?? missing.type}
              benefit={missing.benefit}
              to="/channels"
            />
          ))}
          {/* Only when the platform list loaded and is empty: an older
              interlude response without the list says nothing either way. */}
          {hasEcModuleQuery.data?.ecPlatforms?.length === 0 ? (
            <MissingSourceRow
              icon={<ShoppingBagOutlined />}
              name={t(
                "dataHub.sources.ecommerce.title",
                "E-commerce platforms",
              )}
              benefit={t(
                "dataHub.flow.missing.ecommerce",
                "Bring purchase totals into each customer's profile",
              )}
              to="/applications"
            />
          ) : null}
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.more", "More sources, via API")}
          </Typography>
          {moreSources.map((source) => (
            <Box key={source.key} sx={styles.moreRow}>
              <Box component="span" sx={styles.missingIcon}>
                {source.icon}
              </Box>
              <Box sx={styles.missingText}>
                <Typography variant="body" color={theme.colors.staticFgBody}>
                  {source.name}
                </Typography>
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {source.benefit}
                </Typography>
              </Box>
            </Box>
          ))}
          {organizationSettingQuery.data?.openApiV2Enabled ? (
            <RouterLink to="/applications/api-keys" variant="body2">
              {t("dataHub.flow.more.openApi", "Set up with OpenAPI")}
            </RouterLink>
          ) : (
            <ExternalLink href={t("newRequest.link")} trailingIcon>
              {t("dataHub.flow.more.contact", "Ask us how to connect")}
            </ExternalLink>
          )}
        </Box>
        <Box sx={styles.arrow} aria-hidden="true">
          <ArrowForwardOutlined />
        </Box>
        <Box sx={styles.hub}>
          <Box sx={styles.hubIcon}>
            <PeopleAltOutlined />
          </Box>
          <Typography variant="body" fontWeight={600}>
            {t("dataHub.flow.hub", "One profile per customer")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.mergedBy", "Merged by")}
          </Typography>
          <Box sx={styles.keys}>
            {scope.unifyKeys.map((key) => (
              <Badge key={key.attribute} variant="plain">
                <UnifyKeyLabel unifyKey={key.attribute} />
              </Badge>
            ))}
          </Box>
        </Box>
        <Box sx={styles.arrow} aria-hidden="true">
          <ArrowForwardOutlined />
        </Box>
        <Box sx={styles.column}>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("dataHub.flow.destinations", "Used in")}
          </Typography>
          {hasMaac ? (
            <Row icon={<CampaignOutlined />}>
              {t("dataHub.flow.maac", "MAAC segments, broadcasts and journeys")}
            </Row>
          ) : null}
          {hasCaac ? (
            <Row icon={<HeadsetMicOutlined />}>
              {t("dataHub.flow.caac", "CAAC customer view in conversations")}
            </Row>
          ) : null}
          {adPlatforms.length > 0 ? (
            <Row icon={<CampaignOutlined />}>
              {t("dataHub.flow.ads", {
                platforms: adPlatforms.join(", "),
                defaultValue: "Ad audiences: {{platforms}}",
              })}
            </Row>
          ) : null}
          {apiAccess.length > 0 ? (
            <Row icon={<CodeOutlined />}>
              {t("dataHub.flow.api", {
                tools: apiAccess.join(" & "),
                defaultValue: "Your systems and AI via {{tools}}",
              })}
            </Row>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
